import * as React from 'react';
import * as RechartsPrimitive from 'recharts';

import { cn } from '@/Domain/utils/cn';

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: '', dark: '.dark' } as const;

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  );
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }

  return context;
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    config: ChartConfig;
    children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'];
    fluidHeight?: boolean;
  }
>(({ id, className, children, config, fluidHeight = false, ...props }, ref) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn(
          "flex justify-center text-xs \
           [&_.recharts-cartesian-axis-tick_text]:fill-neutral-500 \
           dark:[&_.recharts-cartesian-axis-tick_text]:fill-neutral-400 \
           [&_.recharts-cartesian-grid_line]:stroke-neutral-200/60 \
           dark:[&_.recharts-cartesian-grid_line]:stroke-neutral-800/60 \
           [&_.recharts-cartesian-grid-horizontal_line]:stroke-neutral-200/60 \
           dark:[&_.recharts-cartesian-grid-horizontal_line]:stroke-neutral-800/60 \
           [&_.recharts-cartesian-grid-vertical_line]:stroke-neutral-200/40 \
           dark:[&_.recharts-cartesian-grid-vertical_line]:stroke-neutral-800/40 \
           [&_.recharts-curve.recharts-tooltip-cursor]:stroke-neutral-300 \
           dark:[&_.recharts-curve.recharts-tooltip-cursor]:stroke-neutral-700 \
           [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-neutral-200/30 \
           dark:[&_.recharts-rectangle.recharts-tooltip-cursor]:fill-neutral-800/30 \
           [&_.recharts-dot[stroke='#fff']]:stroke-transparent \
           [&_.recharts-layer]:outline-none \
           [&_.recharts-polar-grid_line]:stroke-neutral-300 \
           dark:[&_.recharts-polar-grid_line]:stroke-neutral-700 \
           [&_.recharts-reference-line_line]:stroke-neutral-300 \
           dark:[&_.recharts-reference-line_line]:stroke-neutral-700 \
           [&_.recharts-sector[stroke='#fff']]:stroke-transparent \
           [&_.recharts-sector]:outline-none \
           [&_.recharts-surface]:outline-none",
          fluidHeight ? 'h-full' : 'aspect-video',
          className,
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = 'Chart';

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(([, config]) => config.theme || config.color);

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color = itemConfig.theme?.[theme as keyof typeof itemConfig.theme] || itemConfig.color;
    return color ? `  --color-${key}: ${color};` : null;
  })
  .join('\n')}
}
`,
          )
          .join('\n'),
      }}
    />
  );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<'div'> & {
      hideLabel?: boolean;
      hideIndicator?: boolean;
      indicator?: 'line' | 'dot' | 'dashed';
      nameKey?: string;
      labelKey?: string;
    }
>(
  (
    {
      active,
      payload,
      className,
      indicator = 'dot',
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      color,
      nameKey,
      labelKey,
    },
    ref,
  ) => {
    const { config } = useChart();

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) {
        return null;
      }

      const [item] = payload;
      const key = `${labelKey || item?.dataKey || item?.name || 'value'}`;
      const itemConfig = getPayloadConfigFromPayload(config, item, key);
      const value =
        !labelKey && typeof label === 'string'
          ? config[label as keyof typeof config]?.label || label
          : itemConfig?.label;

      if (labelFormatter) {
        return (
          <div className={cn('font-medium text-popover-foreground', labelClassName)}>
            {labelFormatter(value, payload)}
          </div>
        );
      }

      if (!value) {
        return null;
      }

      return (
        <div className={cn('font-medium text-popover-foreground', labelClassName)}>{value}</div>
      );
    }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey]);

    if (!active || !payload?.length) {
      return null;
    }

    const nestLabel = payload.length === 1 && indicator !== 'dot';

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-lg border border-border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-xl supports-[backdrop-filter]:bg-popover/95',
          className,
        )}
      >
        {!nestLabel ? tooltipLabel : null}
        <div className="space-y-1.5">
          {payload.map((item, index) => {
            const key = `${nameKey || item.name || item.dataKey || 'value'}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor = color || item.payload.fill || item.color;

            return (
              <div key={item.dataKey} className="flex items-center gap-2">
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, item.payload)
                ) : (
                  <>
                    {itemConfig?.icon
                      ? (() => {
                          const IconComp = itemConfig.icon as React.ComponentType<{
                            className?: string;
                          }>;
                          return (
                            <IconComp className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
                          );
                        })()
                      : !hideIndicator && (
                          <div
                            className={cn('shrink-0 border-[--color-border] bg-[--color-bg]', {
                              'h-2.5 w-2.5 rounded-full': indicator === 'dot',
                              'w-1 h-4 rounded-[2px]': indicator === 'line',
                              'w-0 h-4 border-[1.5px] border-dashed bg-transparent rounded-[2px]':
                                indicator === 'dashed',
                            })}
                            style={
                              {
                                '--color-bg': indicatorColor,
                                '--color-border': indicatorColor,
                              } as React.CSSProperties
                            }
                          />
                        )}
                    <div className="flex flex-col">
                      {nestLabel ? tooltipLabel : null}
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">
                          {itemConfig?.label || item.name}
                        </span>
                        {item.value !== undefined && item.value !== null && (
                          <span className="font-mono font-medium tabular-nums text-popover-foreground">
                            {Number(item.value).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
ChartTooltipContent.displayName = 'ChartTooltip';

const ChartLegend = RechartsPrimitive.Legend;

const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> &
    Pick<RechartsPrimitive.LegendProps, 'payload' | 'verticalAlign'> & {
      hideIcon?: boolean;
      nameKey?: string;
      valueMap?: Record<string, number>;
      valueFormatter?: (value: number) => React.ReactNode;
    }
>(
  (
    {
      className,
      hideIcon = false,
      payload,
      verticalAlign = 'bottom',
      nameKey,
      valueMap,
      valueFormatter,
    },
    ref,
  ) => {
    const { config } = useChart();

    if (!payload?.length) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-center gap-4',
          verticalAlign === 'top' ? 'pb-3' : 'pt-3',
          className,
        )}
      >
        {payload.map((item) => {
          const key = `${nameKey || (item as any).dataKey || 'value'}`;
          const dataKey = ((nameKey &&
            typeof (item as any)[nameKey] === 'string' &&
            (item as any)[nameKey]) ||
            (typeof (item as any).dataKey === 'string' && (item as any).dataKey) ||
            (typeof (item as any).value === 'string' && (item as any).value) ||
            key) as string;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);

          return (
            <div
              key={item.value}
              className={cn(
                'flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-neutral-500 dark:[&>svg]:text-neutral-400',
              )}
            >
              {itemConfig?.icon && !hideIcon ? (
                (() => {
                  const IconComp = itemConfig.icon as React.ComponentType<{ className?: string }>;
                  return <IconComp className="h-3 w-3 text-neutral-500 dark:text-neutral-400" />;
                })()
              ) : (
                <div
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{
                    backgroundColor: item.color,
                  }}
                />
              )}
              <span>{itemConfig?.label}</span>
              {valueMap && Object.prototype.hasOwnProperty.call(valueMap, dataKey) ? (
                <span className="ml-2 font-mono font-medium tabular-nums text-neutral-950 dark:text-neutral-50">
                  {valueFormatter
                    ? valueFormatter(valueMap![dataKey])
                    : Number(valueMap![dataKey]).toLocaleString()}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  },
);
ChartLegendContent.displayName = 'ChartLegend';

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }

  const payloadPayload =
    'payload' in payload && typeof payload.payload === 'object' && payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (key in payload && typeof payload[key as keyof typeof payload] === 'string') {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === 'string'
  ) {
    configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string;
  }

  return configLabelKey in config ? config[configLabelKey] : config[key as keyof typeof config];
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
};
